import {
  EMAIL_TEMPLATE_NAMES,
  approverFor,
  type ApprovalStage,
  type SubmissionListItem,
  type SubmissionType,
} from '@jfh/intake-shared';
import type { ActivityRow } from '../lib/api.js';

/**
 * Plain words for applications (F10 P1): who it's waiting for, what happened,
 * and what each activity line means — instead of stage names and action codes.
 */

/** The team that decides a stage, as staff say it. */
export function teamName(stage: ApprovalStage): string {
  return stage === 'cs' ? 'Customer Service' : stage === 'accounts' ? 'Accounts' : 'the supplier team';
}

export type PillTone = 'new' | 'pending' | 'approved' | 'declined' | 'lead' | 'neutral';

export interface StatusPill {
  label: string;
  tone: PillTone;
}

/** The last iQ result, as far as the wording needs it. */
type IqResult = { ok: boolean; stubbed: boolean } | null | undefined;
type StatusInput = Pick<SubmissionListItem, 'type' | 'stage' | 'timing'> & { iq?: IqResult };

/** The one status pill an application shows. */
export function applicationStatus(s: StatusInput): StatusPill {
  if (s.type === 'lead') {
    return s.iq?.ok && !s.iq.stubbed ? { label: 'Added to iQ', tone: 'approved' } : { label: 'Received', tone: 'lead' };
  }
  const acting = approverFor(s.type, s.stage);
  if (acting) {
    const team = teamName(acting);
    if (s.timing?.overdue) {
      const short = team.replace(/^the /, '');
      return { label: `Overdue · ${short.charAt(0).toUpperCase()}${short.slice(1)}`, tone: 'declined' };
    }
    return { label: `Waiting for ${team}`, tone: acting === 'accounts' ? 'pending' : 'new' };
  }
  switch (s.stage) {
    case 'completed':
      return {
        label: s.type === 'credit_account' ? 'Credit account set up' : s.type === 'supplier_account' ? 'Supplier set up' : 'Account set up',
        tone: 'approved',
      };
    case 'credit_declined':
      return { label: 'Credit declined', tone: 'declined' };
    default:
      return { label: 'Declined', tone: 'declined' };
  }
}

/** At most one problem pill: iQ first (it blocks the account), then Teams. */
export function problemPill(s: Pick<SubmissionListItem, 'teamsCardError'> & { iq?: IqResult }): string | null {
  if (s.iq && !s.iq.ok && !s.iq.stubbed) return 'Not in iQ';
  if (s.teamsCardError) return 'Teams card didn’t post';
  return null;
}

/** "Trade account", "Website enquiry"… */
export function applicationType(type: SubmissionType): string {
  return {
    trade_account: 'Trade account',
    credit_account: 'Credit account',
    supplier_account: 'Supplier account',
    lead: 'Website enquiry',
  }[type];
}

export interface ActivityLine {
  text: string;
  icon: string;
  tone: 'ok' | 'bad' | 'plain';
  /** Extra words under the line (a decline reason, iQ's message). */
  note?: string | null;
}

function json(s: string | null): Record<string, unknown> {
  if (!s) return {};
  try {
    const v = JSON.parse(s) as unknown;
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

const ENTITY: Record<string, string> = { customer: 'Customer', supplier: 'Supplier', lead: 'Lead' };

/** One activity row as a plain sentence. */
export function activityLine(row: ActivityRow): ActivityLine {
  const before = json(row.before);
  const after = json(row.after);
  const meta = json(row.metadata);
  const iq = /^iq\.(\w+)\.(created|updated)$/.exec(row.action);
  if (iq) {
    const entity = ENTITY[iq[1]!] ?? 'Record';
    const code = str(after.code);
    if (after.stubbed) return { text: 'iQ isn’t connected here, so nothing was sent', icon: 'db', tone: 'plain' };
    if (after.ok === false) {
      return {
        text: iq[2] === 'updated' ? `iQ couldn’t update ${entity.toLowerCase()} ${code ?? ''}`.trim() : `iQ couldn’t set the ${entity.toLowerCase()} up`,
        icon: 'db',
        tone: 'bad',
        note: str(after.message) ? `iQ said: ${str(after.message)}` : null,
      };
    }
    return { text: `${entity}${code ? ` ${code}` : ''} ${iq[2]} in iQ`, icon: 'db', tone: 'ok' };
  }
  switch (row.action) {
    case 'submission.received':
      return { text: 'Application received', icon: 'user', tone: 'plain' };
    case 'approval.decided': {
      const approved = meta.decision === 'approve';
      const credit = before.stage === 'pending_credit';
      const text = credit ? (approved ? 'Credit approved' : 'Credit declined') : approved ? 'Approved' : 'Declined';
      return { text, icon: approved ? 'check' : 'x', tone: approved ? 'ok' : 'bad', note: approved ? null : str(meta.reason) };
    }
    case 'approval.reminded':
      return { text: `Reminder sent to ${str(after.stageLabel) ?? 'the approvers'}`, icon: 'clock', tone: 'plain' };
    case 'card.posted': {
      const channel = str(after.channel);
      const to = channel ? ` to ${channel}` : '';
      if (meta.stubbed) {
        return { text: `Teams card not sent: ${channel ?? 'that channel'} isn’t connected here`, icon: 'teams', tone: 'plain' };
      }
      if (after.delivered === false) {
        return { text: `Teams card didn’t post${to}`, icon: 'teams', tone: 'bad', note: str(meta.error) };
      }
      return { text: `${meta.reminder ? 'Reminder card' : 'Card'} posted${to} in Teams`, icon: 'teams', tone: 'plain' };
    }
    case 'teams.notified': {
      const sent = typeof after.sent === 'number' ? after.sent : 0;
      const who = str(after.stageLabel) ?? 'approvers';
      return sent
        ? { text: `${who} told in Teams (${sent} ${sent === 1 ? 'person' : 'people'})`, icon: 'teams', tone: 'plain' }
        : { text: `Nobody in ${who} could be told in Teams`, icon: 'teams', tone: 'plain' };
    }
    case 'email.sent': {
      const key = str(after.template);
      const name = key && key in EMAIL_TEMPLATE_NAMES ? EMAIL_TEMPLATE_NAMES[key as keyof typeof EMAIL_TEMPLATE_NAMES] : key ?? 'an email';
      const status = str(after.status) ?? '';
      if (status === 'sent') return { text: `Email sent: ${name}`, icon: 'mail', tone: 'plain' };
      if (status === 'disabled') return { text: `No email sent: “${name}” is switched off`, icon: 'mail', tone: 'plain' };
      if (status === 'stubbed') return { text: `Email not sent (email isn’t set up here): ${name}`, icon: 'mail', tone: 'plain' };
      return { text: `Email didn’t send: ${name}`, icon: 'mail', tone: 'bad' };
    }
    case 'field.amended': {
      const n = Object.keys(after).length;
      const where = meta.via === 'teams-card' ? ' on the Teams card' : '';
      return { text: `Corrected ${n || 'some'} ${n === 1 ? 'answer' : 'answers'}${where}`, icon: 'pencil', tone: 'plain' };
    }
    default:
      return { text: row.action, icon: 'dot', tone: 'plain' };
  }
}
